import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, ExternalLink } from 'lucide-react';
import { Action, EmptyState, ErrorNote, FilterChips, PageBody, PageHead, Panel } from '../../components/aa';
import {
  AmountText, InvoiceStatus, KIND_LABEL, KindMark, invoiceDate,
} from '../../components/settings/invoiceParts';
import { billingApi, type InvoiceKind, type StripeInvoiceRow } from '../../services/billingApi';

const PAGE_SIZE = 20;

type Filter = 'all' | InvoiceKind;

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'plan', label: 'Plan' },
  { id: 'seats', label: 'Seats added' },
  { id: 'credit', label: 'Credits' },
  { id: 'topup', label: 'Top-ups' },
];

const dollars = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const openBilling = () => window.dispatchEvent(new CustomEvent('settings:open', { detail: { section: 'payment' } }));

const Tile: React.FC<{ label: string; value: React.ReactNode; note: string }> = ({ label, value, note }) => (
  <div className="rounded-panel border border-line bg-white px-5 py-4">
    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-eyebrow">{label}</div>
    <div className="mt-1.5 text-[24px] font-bold leading-[30px] tracking-[-0.01em] text-ink tabular-nums">{value}</div>
    <div className="mt-0.5 text-[12px] text-ink-dim">{note}</div>
  </div>
);

const ReceiptLinks: React.FC<{ invoice: StripeInvoiceRow }> = ({ invoice }) => (
  <span className="flex items-center justify-end gap-1">
    {invoice.url && (
      <a
        href={invoice.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open the receipt for ${invoice.title}, ${invoiceDate(invoice.created)}`}
        className="inline-flex h-9 items-center gap-1.5 rounded-act px-2.5 text-[12.5px] font-medium text-ink transition-colors hover:bg-canvas-inset"
      >
        <ExternalLink size={14} aria-hidden /> Receipt
      </a>
    )}
    {invoice.pdf && (
      <a
        href={invoice.pdf}
        target="_blank"
        rel="noreferrer"
        aria-label={`Download the PDF for ${invoice.title}, ${invoiceDate(invoice.created)}`}
        className="inline-flex h-9 items-center gap-1.5 rounded-act px-2.5 text-[12.5px] font-medium text-ink transition-colors hover:bg-canvas-inset"
      >
        <Download size={14} aria-hidden /> PDF
      </a>
    )}
  </span>
);

const SkeletonRows: React.FC = () => (
  <div aria-busy="true" aria-label="Loading invoices" className="divide-y divide-line-soft">
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className="flex items-center gap-3 px-6 py-4">
        <span className="h-9 w-9 shrink-0 animate-pulse rounded-tile bg-canvas-inset" />
        <span className="flex-1 space-y-2">
          <span className="block h-3 w-40 animate-pulse rounded bg-canvas-inset" />
          <span className="block h-3 w-64 max-w-full animate-pulse rounded bg-canvas-inset" />
        </span>
        <span className="h-3 w-16 animate-pulse rounded bg-canvas-inset" />
      </div>
    ))}
  </div>
);

/* Every invoice the company has: plan renewals, seat changes, credits and
 * top-ups, newest first. Billing shows the latest few and links here. */
const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<StripeInvoiceRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async (after: string | null) => {
    const { data } = await billingApi.listInvoices(PAGE_SIZE, after);
    setInvoices((current) => (after ? [...current, ...data.invoices] : data.invoices));
    setCursor(data.next_cursor);
    setHasMore(data.has_more);
  }, []);

  const firstLoad = useCallback(() => {
    setLoading(true);
    setFailed(false);
    load(null).catch(() => setFailed(true)).finally(() => setLoading(false));
  }, [load]);

  useEffect(() => { firstLoad(); }, [firstLoad]);

  const loadMore = () => {
    setLoadingMore(true);
    setMoreFailed(false);
    load(cursor).catch(() => setMoreFailed(true)).finally(() => setLoadingMore(false));
  };

  const counts = useMemo(() => {
    const byKind: Record<string, number> = { all: invoices.length };
    invoices.forEach((inv) => { byKind[inv.kind] = (byKind[inv.kind] || 0) + 1; });
    return byKind;
  }, [invoices]);

  const month = useMemo(() => {
    const now = new Date();
    const thisMonth = invoices.filter((inv) => {
      const d = new Date(inv.created * 1000);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    const paid = thisMonth.filter((inv) => inv.status === 'paid' && inv.total_cents > 0).reduce((sum, inv) => sum + inv.total_cents, 0);
    const credited = thisMonth.filter((inv) => inv.total_cents < 0).reduce((sum, inv) => sum - inv.total_cents, 0);
    const due = invoices.filter((inv) => inv.status === 'open').reduce((sum, inv) => sum + inv.total_cents, 0);
    return { paid, credited, due, name: now.toLocaleDateString(undefined, { month: 'long' }) };
  }, [invoices]);

  const shown = invoices.filter((inv) => filter === 'all' || inv.kind === filter);
  const tabs = FILTERS.filter((f) => f.id === 'all' || counts[f.id]).map((f) => ({ id: f.id, label: f.label, count: counts[f.id] }));

  let body: React.ReactNode = null;
  if (loading) body = <SkeletonRows />;
  if (!loading && failed && invoices.length === 0) {
    body = (
      <div className="px-6 py-8">
        <ErrorNote message="Could not load invoices from Stripe." />
        <div className="mt-3"><Action size="sm" variant="outline" onClick={firstLoad}>Try again</Action></div>
      </div>
    );
  }
  if (!loading && !failed && invoices.length === 0) {
    body = (
      <EmptyState
        title="No invoices yet"
        reason="Your first invoice appears when you move to the Team plan or add funds to your prepaid balance."
        action={<Action variant="outline" onClick={openBilling}>Open Billing</Action>}
      />
    );
  }
  if (!loading && invoices.length > 0 && shown.length === 0) {
    body = <EmptyState title="Nothing in this view" reason="No invoices of this type among the ones loaded." />;
  }
  if (!loading && shown.length > 0) {
    body = (
      <>
        <table className="hidden w-full border-collapse text-left md:table">
          <caption className="sr-only">Invoices, newest first</caption>
          <thead>
            <tr className="border-b border-line text-[11px] font-semibold uppercase tracking-[0.1em] text-eyebrow">
              <th scope="col" className="px-6 py-3 font-semibold">Date</th>
              <th scope="col" className="px-3 py-3 font-semibold">Invoice</th>
              <th scope="col" className="px-3 py-3 font-semibold">Type</th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">Amount</th>
              <th scope="col" className="px-3 py-3 font-semibold">Status</th>
              <th scope="col" className="px-6 py-3 text-right font-semibold"><span className="sr-only">Receipts</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((inv) => (
              <tr key={inv.id} className="border-b border-line-soft last:border-b-0 hover:bg-canvas">
                <td className="whitespace-nowrap px-6 py-3.5 text-[13px] tabular-nums text-ink-muted">{invoiceDate(inv.created)}</td>
                <td className="px-3 py-3.5">
                  <span className="flex items-center gap-3">
                    <KindMark kind={inv.kind} />
                    <span className="min-w-0">
                      <span className="block text-[13.5px] font-semibold text-ink">{inv.title}</span>
                      <span className="block text-[12px] text-ink-dim">{inv.detail}</span>
                    </span>
                  </span>
                </td>
                <td className="px-3 py-3.5 text-[13px] text-ink-muted">{KIND_LABEL[inv.kind]}</td>
                <td className="px-3 py-3.5 text-right"><AmountText cents={inv.total_cents} className="text-[13.5px] font-semibold" /></td>
                <td className="px-3 py-3.5"><InvoiceStatus invoice={inv} /></td>
                <td className="px-6 py-3.5"><ReceiptLinks invoice={inv} /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <ul className="m-0 list-none p-0 md:hidden">
          {shown.map((inv) => (
            <li key={inv.id} className="border-b border-line-soft px-4 py-4 last:border-b-0">
              <div className="flex items-start gap-3">
                <KindMark kind={inv.kind} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-[14px] font-semibold text-ink">{inv.title}</span>
                    <AmountText cents={inv.total_cents} className="text-[14px] font-semibold" />
                  </div>
                  <div className="mt-0.5 text-[12.5px] text-ink-dim">{invoiceDate(inv.created)} · {inv.detail}</div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <InvoiceStatus invoice={inv} />
                    <ReceiptLinks invoice={inv} />
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <PageBody>
      <PageHead
        eyebrow="Billing"
        title="Invoices"
        sub="Every plan charge, seat change, credit and top-up, newest first. Receipts open on Stripe."
        actions={<Action variant="outline" onClick={openBilling}>Back to Billing</Action>}
      />

      {!loading && invoices.length > 0 && (
        <div className="mb-5 grid gap-3 sm:grid-cols-3">
          <Tile label={`Paid in ${month.name}`} value={dollars(month.paid)} note="Plan, seats and top-ups" />
          <Tile label={`Credited in ${month.name}`} value={<span className={month.credited ? 'text-[#3F7A2F]' : ''}>{dollars(month.credited)}</span>} note="Taken off your next invoice" />
          <Tile label="Due now" value={dollars(month.due)} note={month.due ? 'Open on Stripe to pay' : 'Nothing outstanding'} />
        </div>
      )}

      <Panel>
        {!loading && invoices.length > 0 && (
          <div className="border-b border-line px-4 py-3 sm:px-6">
            <FilterChips tabs={tabs} active={filter} onChange={(id) => setFilter(id as Filter)} label="Invoice type" />
          </div>
        )}
        {body}
        {hasMore && !loading && (
          <div className="flex flex-wrap items-center justify-center gap-3 border-t border-line-soft px-6 py-4">
            <Action variant="outline" onClick={loadMore} disabled={loadingMore} reason="Loading">
              {loadingMore ? 'Loading' : 'Load older invoices'}
            </Action>
            {moreFailed && <span role="alert" className="text-[12.5px] text-danger">Couldn't load older invoices. Try again.</span>}
          </div>
        )}
      </Panel>
      {!loading && invoices.length > 0 && (
        <p className="mt-3 text-[12px] text-ink-dim">
          Showing {shown.length} of {invoices.length} loaded{hasMore ? '. Older ones load below' : ''}. Credits show in green with a + and come off your next invoice.
        </p>
      )}
    </PageBody>
  );
};

export default InvoicesPage;
